// Pure helpers around EditorJS documents used by the article creation wizard.
// Extracted from CreateArticle.js; behavior pinned by editorBlocks.test.js.
import { EDITORJS_VERSION, parseMarkdownToEditorJS } from './markdownToEditorJS';

export const createEmptyDraft = () => ({ time: Date.now(), blocks: [], version: EDITORJS_VERSION });

export function extractPlainTextFromEditorBlocks(blocks = []) {
  return blocks
    .map((block) => {
      if (!block || typeof block !== 'object') return '';
      if (typeof block?.data?.text === 'string') return block.data.text;
      if (Array.isArray(block?.data?.items)) return block.data.items.join(' ');
      if (Array.isArray(block?.data?.content)) {
        return block.data.content.flat().join(' ');
      }
      if (typeof block?.data?.code === 'string') return block.data.code;
      return '';
    })
    .map((item) => item.replace(/<[^>]*>/g, '').trim())
    .filter(Boolean)
    .join('\n')
    .trim();
}

export const buildKeywordContextFromDraft = (draftRaw) => {
  try {
    const parsed = typeof draftRaw === 'string' ? JSON.parse(draftRaw) : draftRaw;
    const blocks = Array.isArray(parsed?.blocks) ? parsed.blocks : [];
    const focusedText = extractPlainTextFromEditorBlocks(blocks).replace(/\s+/g, ' ').trim();
    if (!focusedText) return '';
    return focusedText.length > 1200 ? `${focusedText.slice(0, 1200)}...` : focusedText;
  } catch (error) {
    return '';
  }
};

export const hasPersistedArticleBody = (body) => {
  if (!body) return false;
  try {
    const parsed = typeof body === 'string' ? JSON.parse(body) : body;
    return Array.isArray(parsed?.blocks) && parsed.blocks.length > 0;
  } catch (_) {
    return false;
  }
};

/**
 * True when a stored article_draft has at least one block with real content
 * (used when loading an existing article into the wizard).
 */
export const hasGeneratedDraftContent = (article) => {
  let hasGeneratedDraft = false;
  if (article.article_draft) {
    try {
      const draftContent = typeof article.article_draft === 'string'
        ? JSON.parse(article.article_draft)
        : article.article_draft;

      // Consider draft generated if it has blocks with actual content
      if (draftContent.blocks && draftContent.blocks.length > 0) {
        // The original analysis read every block before checking content, so a null
        // block anywhere marks the draft as not generated. Kept for parity.
        draftContent.blocks.forEach((block) => block.type);

        hasGeneratedDraft = draftContent.blocks.some(block => {
          const hasContent = block.data &&
            (
              (block.data.text && block.data.text.trim().length > 0) ||
              (block.data.items && block.data.items.length > 0) ||
              (block.data.content && block.data.content.trim().length > 0)
            );
          return hasContent;
        });
      }
    } catch (error) {
      hasGeneratedDraft = false;
    }
  }
  return hasGeneratedDraft;
};

/**
 * Wizard step to open for a loaded article (1 = setup, 2 = content, 5 = review).
 */
export const resolveTargetStep = (article, hasGeneratedDraft) => {
  let targetStep = 5; // Default to review step

  if (!article.content_type) {
    targetStep = 1; // Setup step
  } else if (!article.article_context) {
    targetStep = 2; // Content step
  } else if (!hasGeneratedDraft) {
    targetStep = 2; // Draft must be generated in content step
  } else if (!article.keywords || article.keywords.length === 0) {
    targetStep = 2; // Draft preview step (before keywords)
  }
  return targetStep;
};

/**
 * Parses a draft returned by the workers: objects are used as-is, strings are parsed as
 * JSON and fall back to markdown conversion; a missing draft yields `getEmptyDraft()`.
 */
export const parseGeneratedDraft = (rawDraft, markdownFallbackText, getEmptyDraft = createEmptyDraft) => {
  let draftContent;
  if (rawDraft) {
    try {
      if (typeof rawDraft === 'object') {
        draftContent = rawDraft;
      } else {
        draftContent = JSON.parse(rawDraft);
      }
    } catch (e) {
      draftContent = parseMarkdownToEditorJS(rawDraft || markdownFallbackText);
    }
  } else {
    draftContent = getEmptyDraft();
  }
  return draftContent;
};
